/* @flow */
import gql from 'graphql-tag';
import {compose} from 'redux';
import React, {PureComponent, Fragment} from 'react';
import {connect} from 'react-redux';
import {FormattedMessage, injectIntl} from 'react-intl';
import {ButtonPrimary} from '@homeaway/react-buttons';
import ExpandedGroupHitPlaceholder from './ExpandedGroupHitPlaceholder';
import Hit, {fragments as hitFragments, mapHitFragments} from '../Hit/Hit';
import mapActions from '../../actions/map';
import expandedGroupsActions from '../../actions/expandedGroups';
import {triggerExpandedGroupHitClickEDAPEvent} from '../../utils/hitEdapUtils';

import type {IntlShape} from 'react-intl';
import type {MouseLeaveShape as HitMouseLeaveShape} from '../Hit/Hit';
import type {ListingShape as HitListingShape} from '../Hit/Hit';
import type {State as ReduxState} from '../../reducers';
import type {MapViewport} from '../MapContainer/MapContainer';
import type {SearchParamsType} from '../../reducers/searchParams';

export const fragments = {
    ExpandedGroup: gql`
        fragment ExpandedGroupExpandedGroup on ExpandedGroup {
            listings {
                ...HitListing
                ...MapHitListing
            }
            mapViewport {
                neLat
                neLong
                swLat
                swLong
            }
        }
        ${hitFragments.Listing}
        ${mapHitFragments.Listing}
    `
};

export type Shape = {
    listings: HitListingShape[],
    mapViewport: MapViewport
};

type StateProps = {
    isExpanded: boolean,
    inputParams: SearchParamsType
};

type DispatchProps = {
    syncBounds: typeof mapActions.syncBounds,
    expandGroup: typeof expandedGroupsActions.expandGroup
};

type OwnProps = {
    onHitMouseEnter: (string) => void,
    onHitMouseLeave: (HitMouseLeaveShape) => void,
    expandedGroup: Shape,
    index: number
};

type Props = DispatchProps & OwnProps & StateProps & {intl: IntlShape};

export class ExpandedGroup extends PureComponent<Props> {
    expand = () => {
        const {
            expandedGroup: {mapViewport},
            index,
            syncBounds,
            expandGroup
        } = this.props;
        if (mapViewport) {
            syncBounds({...mapViewport}, {fitBoundsOverride: true});
            expandGroup({index, hasModifiedMapViewport: true});
        } else {
            expandGroup({index});
        }
    };

    render() {
        const {
            expandedGroup: {listings},
            intl,
            isExpanded,
            onHitMouseEnter,
            onHitMouseLeave,
            inputParams
        } = this.props;

        const listingsCount = listings.length;

        if (!listingsCount) {
            return null;
        }

        const message = (
            <FormattedMessage
                id="expandedSearchNearbyLabel"
                defaultMessage="You might like these nearby properties"
            />
        );

        const buttonLabel = intl.formatMessage(
            {
                id: 'expandedSearchNearbyButtonLabel',
                defaultMessage:
                    'View {count, plural, one {{count} nearby property} few {{count} nearby properties} many {{count} nearby properties} other {{count} nearby properties}}'
            },
            {
                count: listingsCount
            }
        );
        return (
            <Fragment>
                <dt className={'ExpandedGroup__label'}>{message}</dt>
                <dd className="ExpandedGroup__content">
                    {isExpanded ? (
                        <div className="ExpandedGroup__hits">
                            {listings.map((listing, index) => {
                                return (
                                    <Hit
                                        {...{
                                            key: listing.listingId,
                                            index,
                                            hitIndex: index,
                                            listing,
                                            onHitClick: triggerExpandedGroupHitClickEDAPEvent,
                                            onMouseEnter: onHitMouseEnter,
                                            onMouseLeave: onHitMouseLeave,
                                            inputParams
                                        }}
                                    />
                                );
                            })}
                        </div>
                    ) : (
                        <div className="ExpandedGroup__card">
                            <div className={'ExpandedGroup__button'}>
                                <ButtonPrimary
                                    onClick={this.expand}
                                    label={buttonLabel}
                                />
                            </div>
                            <ExpandedGroupHitPlaceholder
                                listingData={listings[0]}
                                index={0}
                                inputParams={inputParams}
                            />
                        </div>
                    )}
                </dd>
            </Fragment>
        );
    }
}

const mapStateToProps = (
    {
        expandedGroups: {openGroupIndexes},
        searchParams: {inputParams}
    }: ReduxState,
    {index}: OwnProps
) => ({
    isExpanded: openGroupIndexes.includes(index),
    inputParams
});

const mapDispatchToProps = {
    expandGroup: expandedGroupsActions.expandGroup,
    syncBounds: mapActions.syncBounds
};

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    injectIntl
)(ExpandedGroup);
